import { supabase } from "@/integrations/supabase/client";
import { DailyProgress } from "./gameProgress";
import { hashSolution } from "./solutionHash";

export interface CloudProgress extends DailyProgress {
  puzzle_date: string;
}

// Ensure profile and game_stats exist for user (auto-creates if missing)
export const ensureUserProfile = async (userId: string): Promise<boolean> => {
  try {
    // Check if profile exists
    const { data: profile } = await supabase
      .from('profiles')
      .select('id')
      .eq('id', userId)
      .maybeSingle();

    if (!profile) {
      // Get user email from auth
      const { data: { user } } = await supabase.auth.getUser();
      
      // Create profile
      const { error: profileError } = await supabase
        .from('profiles')
        .insert({
          id: userId,
          display_name: null,
          email: user?.email || null
        });

      if (profileError) {
        console.error('Failed to create profile:', profileError);
        return false;
      }
      console.log('✅ Created missing profile for user:', userId);
    }

    // Check if game_stats exists
    const { data: stats } = await supabase
      .from('game_stats')
      .select('user_id')
      .eq('user_id', userId)
      .maybeSingle();

    if (!stats) {
      const { error: statsError } = await supabase
        .from('game_stats')
        .insert({ user_id: userId });

      if (statsError) {
        console.error('Failed to create game_stats:', statsError);
        return false;
      }
      console.log('✅ Created missing game_stats for user:', userId);
    }

    return true;
  } catch (error) {
    console.error('Error ensuring user profile:', error);
    return false;
  }
};

export const syncProgressToCloud = async (
  userId: string,
  date: Date,
  progress: DailyProgress
): Promise<void> => {
  const dateStr = date.toISOString().split('T')[0];
  
  // Ensure profile exists before syncing
  await ensureUserProfile(userId);
  
  // Hash the solution before storing
  const solutionHash = progress.solution ? await hashSolution(progress.solution) : null;
  
  const { error } = await supabase
    .from('game_progress')
    .upsert({
      user_id: userId,
      puzzle_date: dateStr,
      completed: progress.completed,
      won: progress.won,
      attempts: progress.attempts,
      hints_used: progress.hintsUsed,
      guesses: progress.guesses,
      solution: null, // No longer store plaintext
      solution_hash: solutionHash,
      revealed_positions: progress.revealedPositions || {},
      completed_at: progress.completedAt ? new Date(progress.completedAt).toISOString() : null
    }, {
      onConflict: 'user_id,puzzle_date'
    });

  if (error) {
    console.error('Failed to sync progress to cloud:', error);
    throw error;
  }
};

export const loadProgressFromCloud = async (
  userId: string,
  date: Date
): Promise<DailyProgress | null> => {
  const dateStr = date.toISOString().split('T')[0];
  
  const { data, error } = await supabase
    .from('game_progress')
    .select('*')
    .eq('user_id', userId)
    .eq('puzzle_date', dateStr)
    .maybeSingle();

  if (error) {
    console.error('Failed to load progress from cloud:', error);
    return null;
  }

  if (!data) return null;

  return {
    date: dateStr,
    completed: data.completed,
    won: data.won,
    attempts: data.attempts,
    hintsUsed: data.hints_used,
    guesses: data.guesses,
    solution: data.solution, // May be null now
    completedAt: data.completed_at ? new Date(data.completed_at).getTime() : undefined,
    revealedPositions: (data.revealed_positions as Record<number, string>) || {}
  };
};

// Record game result using secure SECURITY DEFINER function
export const recordGameResult = async (
  userId: string,
  date: Date,
  progress: DailyProgress
): Promise<void> => {
  const dateStr = date.toISOString().split('T')[0];
  
  if (!progress.completed) {
    // Only record completed games
    return;
  }

  // Ensure profile exists before recording
  await ensureUserProfile(userId);

  // Hash the solution before sending to server
  const solutionHash = progress.solution ? await hashSolution(progress.solution) : null;

  const { error } = await supabase.rpc('record_game_result', {
    p_puzzle_date: dateStr,
    p_won: progress.won,
    p_attempts: progress.attempts,
    p_hints_used: progress.hintsUsed,
    p_guesses: progress.guesses,
    p_solution: null, // No longer send plaintext
    p_revealed_positions: progress.revealedPositions || {},
    p_solution_hash: solutionHash
  });

  if (error) {
    console.error('Failed to record game result:', error);
    throw error;
  }
};

// Force re-sync all localStorage data to cloud
export const forceSyncToCloud = async (userId: string): Promise<{ success: boolean; message: string }> => {
  try {
    // Ensure profile exists
    const profileCreated = await ensureUserProfile(userId);
    if (!profileCreated) {
      return { success: false, message: 'Failed to create user profile' };
    }

    // Clear migration flag to allow re-migration
    localStorage.removeItem('wordpop-cloud-migrated');
    
    // Run migration
    await migrateLocalStorageToCloud(userId);
    
    // Mark as migrated
    localStorage.setItem('wordpop-cloud-migrated', 'true');
    
    return { success: true, message: 'Data synced to cloud successfully' };
  } catch (error) {
    console.error('Force sync failed:', error);
    return { success: false, message: error instanceof Error ? error.message : 'Unknown error' };
  }
};

// Legacy function - kept for backwards compatibility but deprecated
export const syncStatsToCloud = async (
  userId: string,
  stats: {
    totalPlayed: number;
    totalWon: number;
    currentStreak: number;
    maxStreak: number;
    guessDistribution: Record<number, number>;
  }
): Promise<void> => {
  console.warn('syncStatsToCloud is deprecated. Use recordGameResult instead.');
  // This function is now a no-op as stats are managed by record_game_result
};

export const loadStatsFromCloud = async (userId: string) => {
  const { data, error } = await supabase
    .from('game_stats')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    console.error('Failed to load stats from cloud:', error);
    return null;
  }

  if (!data) return null;

  return {
    totalPlayed: data.total_played,
    totalWon: data.total_won,
    currentStreak: data.current_streak,
    maxStreak: data.max_streak,
    guessDistribution: data.guess_distribution as Record<number, number>
  };
};

// Load ALL cloud progress entries for a user (for Archive calendar)
export const loadAllProgressFromCloud = async (
  userId: string
): Promise<Map<string, { completed: boolean; won: boolean; guessCount: number; hintsUsed: number; attempts: number }>> => {
  const { data, error } = await supabase
    .from('game_progress')
    .select('puzzle_date, completed, won, guesses, hints_used, attempts')
    .eq('user_id', userId);

  if (error) {
    console.error('Failed to load all progress from cloud:', error);
    return new Map();
  }

  const progressMap = new Map<string, { completed: boolean; won: boolean; guessCount: number; hintsUsed: number; attempts: number }>();
  
  for (const row of data || []) {
    progressMap.set(row.puzzle_date, {
      completed: row.completed,
      won: row.won,
      guessCount: row.guesses?.length || 0,
      hintsUsed: row.hints_used || 0,
      attempts: row.attempts || 0
    });
  }

  return progressMap;
};

export const migrateLocalStorageToCloud = async (userId: string): Promise<void> => {
  let migratedCount = 0;

  // NEW: Read from the main wordpop-progress key (contains all dates as JSON object)
  const mainProgressData = localStorage.getItem('wordpop-progress');
  
  if (mainProgressData) {
    try {
      const allProgress = JSON.parse(mainProgressData) as Record<string, DailyProgress>;
      
      for (const [dateStr, progress] of Object.entries(allProgress)) {
        if (!progress) continue;
        
        try {
          const date = new Date(dateStr);
          if (progress.completed) {
            await recordGameResult(userId, date, progress);
          } else {
            await syncProgressToCloud(userId, date, progress);
          }
          migratedCount++;
        } catch (error) {
          console.error(`Failed to migrate progress for ${dateStr}:`, error);
        }
      }
      console.log(`✅ Migrated ${migratedCount} game entries from main progress key`);
    } catch (error) {
      console.error('Failed to parse main progress data:', error);
    }
  }

  // FALLBACK: Also check for individual date keys (legacy format)
  const progressKeys = Object.keys(localStorage).filter(key => 
    key.startsWith('wordpop-progress-')
  );

  for (const key of progressKeys) {
    const dateStr = key.replace('wordpop-progress-', '');
    const data = localStorage.getItem(key);
    if (data) {
      try {
        const progress = JSON.parse(data) as DailyProgress;
        const date = new Date(dateStr);
        if (progress.completed) {
          await recordGameResult(userId, date, progress);
        } else {
          await syncProgressToCloud(userId, date, progress);
        }
        migratedCount++;
      } catch (error) {
        console.error(`Failed to migrate progress for ${dateStr}:`, error);
      }
    }
  }

  if (progressKeys.length > 0) {
    console.log(`✅ Migrated ${progressKeys.length} entries from individual keys`);
  }

  // Trigger server-side stats recalculation
  try {
    await supabase.rpc('recalculate_user_stats', { p_user_id: userId });
    console.log('✅ Triggered stats recalculation');
  } catch (error) {
    console.error('Failed to recalculate stats:', error);
  }

  console.log(`📊 Total migration: ${migratedCount} game entries processed`);
};